<template>
  <iRow class="side-nav" height="fill" overflow="scroll">
    <iMenu
      orientation="vertical"
      :active-item="activePublicationItem"
      :items="filterVisibleSidePanelItems()"
      :menu-item-style-overrides="myStyleOverrides"
    />
  </iRow>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import {
  PAGE_MY_ACTIVE_PUBLICATIONS,
  PAGE_MY_DRAFT_PUBLICATIONS,
  PAGE_MY_INACTIVE_PUBLICATIONS,
  PAGE_CREATE_PUBLICATION,
  PAGE_PUBLICATION_OVERVIEW,
  PAGE_PUBLICATION_DAILY_SUBSCRIPTION_REVENUE,
  PAGE_PUBLICATION_SUBSCRIPTION_PLANS,
  PAGE_PUBLICATION_POSTS,
  PAGE_PUBLICATION_CHAT_ROOM,
  PAGE_PUBLICATION_USERS,
  PAGE_PUBLICATION_SETTINGS,
  PAGE_PUBLICATION_BRANDING,
  PAGE_PUBLICATION_DEPLOYMENT,
  PAGE_PUBLICATION_FEED_GROUP_LIST,
  PAGE_PUBLICATION_PRO_FEATURES,
  PAGE_PUBLICATION_ANALYTICS,
  // PAGE_COLLABORATOR_PROPOSALS,
  // PAGE_MY_COLLABORATOR_PROPOSALS,
  PAGE_PUBLICATION_EVENTS,
  PAGE_PUBLICATION_SUPPORT,
  PAGE_PUBLICATION_CONTRACTS,
  PAGE_PUBLICATION_COURSES,
  SIDE_NAV_STYLE_OVERRIDES,
  PAGE_PUBLICATION_ADVERTISING,
  PAGE_PUBLICATION_COLLABORATORS,
} from "@/constants/nav-constants";
import { useGeneralStore } from "%/stores/general-store";
import { useFeatureStore } from "%/stores/feature-store";
import { useDraftPublicationFeedStore } from "SmartFeed/stores/feed/draft-publication-feed-store";
import { useInactivePublicationFeedStore } from "SmartFeed/stores/feed/inactive-publication-feed-store";
import { useActivePublicationFeedStore } from "SmartFeed/stores/feed/active-publication-feed-store";
import SmartFeedEngine from "@/smart-feed/smart-feed-engine";
import { useCollaboratorStore } from "@/stores/collaborator-store";
import {
  ROLE_CONTRIBUTOR,
  ROLE_MARKETER,
  ROLE_EDITOR,
  ROLE_PRODUCER,
  ROLE_DESIGNER,
  ROLE_ADMIN,
  ROLE_MODERATOR,
  ROLE_SUPPORT,
  COLLABORATOR_NAV_PAGE_STORIES,
  COLLABORATOR_NAV_PAGE_ADVERTISING,
  COLLABORATOR_NAV_PAGE_EVENTS,
  COLLABORATOR_NAV_PAGE_ANALYTICS,
  COLLABORATOR_NAV_PAGE_TEAM,
  COLLABORATOR_NAV_PAGE_CHAT_ROOM,
  COLLABORATOR_NAV_PAGE_SUPPORT,
  COLLABORATOR_NAV_PAGE_MY_USERS,
  COLLABORATOR_NAV_PAGE_REVENUE,
  COLLABORATOR_NAV_PAGE_SUBSCRIPTION_PLANS,
  COLLABORATOR_NAV_PAGE_COURSES,
  COLLABORATOR_NAV_PAGE_SETTINGS,
  COLLABORATOR_NAV_PAGE_BRANDING,
  COLLABORATOR_NAV_PAGE_PRO_FEATURES,
  COLLABORATOR_NAV_PAGE_DEPLOYMENT,
  COLLABORATOR_NAV_PAGE_RSS,
  COLLABORATOR_NAV_ITEM_OVERVIEW,
  COLLABORATOR_NAV_ITEM_CREATE_A_NEW_PUBLICATION,
  COLLABORATOR_NAV_ITEM_INVITE_TO_COLLABORATE,
  COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
  COLLABORATOR_NAV_ITEM_DRAFT_PUBLICATIONS,
  COLLABORATOR_NAV_ITEM_INACTIVE_PUBLICATIONS,
  COLLABORATOR_NAV_ITEM_CONTRACTS,
} from "@/constants/collaborator-constants";


export default {
  name: "PublicationSideNav",
  props: {
    activeItem: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState(useGeneralStore, ["orgId"]),
    ...mapState(useCreatorPublicationStore, ["publicationId"]),
    ...mapState(useCollaboratorStore, [
      "role",
      "isOwner",
      "isEditor",
      "isContributor",
      "isDesigner",
    ]),
    myStyleOverrides() {
      return SIDE_NAV_STYLE_OVERRIDES;
    },
    myActivePublicationsMapped() {
      return this.getPublications(useActivePublicationFeedStore).map(
        this.myPublicationMenuItem,
      );
    },
    myDraftPublicationsMapped() {
      return this.getPublications(useDraftPublicationFeedStore).map(
        this.myPublicationMenuItem,
      );
    },
    myInactivePublicationsMapped() {
      return this.getPublications(useInactivePublicationFeedStore).map(
        this.myPublicationMenuItem,
      );
    },
    isContractFeatureEnabled() {
      return useFeatureStore().isFeatureEnabled("contracts");
    },
    sideNavPublication() {
      let menuItems = [
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_OVERVIEW, this.orgId),
          label: "Overview",
          actionValue: `/publication/org/${this.orgId}/overview`,
          icon: "chart-line",
        },
        {
          name: this.myMenuItemName(PAGE_CREATE_PUBLICATION, this.orgId),
          label: "Create a New Publication",
          actionValue: `/publication/org/${this.orgId}/create`,
          icon: "circle-plus",
        },
        // {
        //   name: this.myMenuItemName(PAGE_COLLABORATOR_PROPOSALS, this.orgId),
        //   label: "Invited to Collaborate",
        //   actionValue: `/publication/org/${this.orgId}/invites`,
        //   icon: "user-plus",
        //   subItems: [
        //     {
        //       name: this.myMenuItemName(
        //         PAGE_MY_COLLABORATOR_PROPOSALS,
        //         this.orgId,
        //       ),
        //       label: "My Proposals",
        //       actionValue: `/publication/org/${this.orgId}/proposals`,
        //       icon: "file-contract",
        //     },
        //   ],
        // },
        {
          name: this.myMenuItemName(PAGE_MY_ACTIVE_PUBLICATIONS, this.orgId),
          label: "Active Publications",
          actionValue: `/publication/org/${this.orgId}/publications/active`,
          icon: "newspaper",
          subItems: this.myActivePublicationsMapped,
        },
        {
          name: this.myMenuItemName(PAGE_MY_DRAFT_PUBLICATIONS, this.orgId),
          label: "Draft Publications",
          actionValue: `/publication/org/${this.orgId}/publications/draft`,
          icon: "pen-to-square",
          subItems: this.myDraftPublicationsMapped,
        },
        {
          name: this.myMenuItemName(PAGE_MY_INACTIVE_PUBLICATIONS, this.orgId),
          label: "Inactive Publications",
          actionValue: `/publication/org/${this.orgId}/publications/inactive`,
          icon: "box",
          subItems: this.myInactivePublicationsMapped,
        },
      ];

      if (this.isContractFeatureEnabled) {
        menuItems = [
          ...menuItems,
          {
            name: this.myMenuItemName(PAGE_PUBLICATION_CONTRACTS, this.orgId),
            label: "Contracts",
            actionValue: `/publication/org/${this.orgId}/contracts`,
            icon: "file-signature",
          },
        ];
      }
      return menuItems;
    },
    activePublicationItem() {
      if (this.publicationId) {
        return `${this.activeItem}_${this.publicationId}`;
      } else if (this.orgId) {
        return `${this.activeItem}_${this.orgId}`;
      } else {
        return this.activeItem;
      }
    },
  },
  methods: {
    ...mapActions(useFeatureStore, ["isFeatureEnabled"]),
    getPublications(feedStore) {
      let publicationFeed = feedStore().feed;
      const entityStore = SmartFeedEngine.getEntityPiniaStore("publication");
      if (publicationFeed.length < 1) {
        feedStore().getSmartFeed();
      }
      const entities = {};
      publicationFeed.forEach(publication => {
        const publicationId = publication.id;
        entities[publicationId] = entityStore().getEntity(publicationId);
      });
      return Object.values(entities);
    },
    myMenuItemName(name, nameSuffix) {
      return `${name}_${nameSuffix}`;
    },
    myPublicationMenuItem(publication) {
      let publicationId =
        publication.publication_id ?? publication.publicationId;

      let publicationMenuItem = {
        name: this.myMenuItemName(publication.name, publicationId),
        label: publication.name,
        actionValue: `/publication/${publicationId}`,
        imageIcon: publication.publicationLogo,
        subItems: this.myPublicationSubMenuItems(publication),
      };

      return publicationMenuItem;
    },
    myPublicationSubMenuItems(publication) {
      let publicationId =
        publication.publication_id ?? publication.publicationId;

      const publicationSubItems = [
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_POSTS, publicationId),
          label: "Stories",
          actionValue: `/publication/${publicationId}/stories`,
          icon: "book-open",
        },
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_ADVERTISING, publicationId),
          label: "Advertising",
          actionValue: `/publication/${publicationId}/advertising`,
          icon: "rectangle-ad",
        },
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_EVENTS, publicationId),
          label: "Events",
          actionValue: `/publication/${publicationId}/events`,
          icon: "calendar-week",
        },
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_ANALYTICS, publicationId),
          label: "Analytics",
          actionValue: `/publication/${publicationId}/analytics`,
          icon: "chart-line",
        },
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_COLLABORATORS, publicationId),
          label: "Team",
          actionValue: `/publication/${publicationId}/collaborators`,
          icon: "regular-handshake",
        },
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_CHAT_ROOM, publicationId),
          label: "Chat Room",
          actionValue: `/publication/${publicationId}/chatroom`,
          icon: "comment",
        },
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_SUPPORT, publicationId),
          label: "Support",
          actionValue: `/publication/${publicationId}/support`,
          icon: "headset",
        },
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_USERS, publicationId),
          label: "My Users",
          actionValue: `/publication/${publicationId}/user-management`,
          icon: "users",
        },
        {
          name: this.myMenuItemName(
            PAGE_PUBLICATION_DAILY_SUBSCRIPTION_REVENUE,
            publicationId,
          ),
          label: "Revenue",
          actionValue: `/publication/${publicationId}/subscription-daily-revenue`,
          icon: "money-bill",
        },
        {
          name: this.myMenuItemName(
            PAGE_PUBLICATION_SUBSCRIPTION_PLANS,
            publicationId,
          ),
          label: "Subscription Plans",
          actionValue: `/publication/${publicationId}/subscription-plans`,
          icon: "dollar",
        },
        {
          name: this.myMenuItemName(
            PAGE_PUBLICATION_COURSES,
            publicationId,
          ),
          label: "Courses",
          actionValue: `/publication/${publicationId}/courses`,
          icon: "graduation-cap",
        },
        {
          name: this.myMenuItemName(PAGE_PUBLICATION_SETTINGS, publicationId),
          label: "Settings",
          actionValue: `/publication/${publicationId}/settings`,
          icon: "gear",
          subItems: [
            {
              name: this.myMenuItemName(
                PAGE_PUBLICATION_BRANDING,
                publicationId,
              ),
              label: "Branding",
              actionValue: `/publication/${publicationId}/branding`,
              icon: "folder-image",
            },
            {
              name: this.myMenuItemName(
                PAGE_PUBLICATION_PRO_FEATURES,
                publicationId,
              ),
              label: "Pro Features",
              actionValue: `/publication/${publicationId}/features/pro`,
              icon: "star",
            },
            {
              name: this.myMenuItemName(
                PAGE_PUBLICATION_DEPLOYMENT,
                publicationId,
              ),
              label: "Deployment",
              actionValue: `/publication/${publicationId}/deployment`,
              icon: "box",
            },
            {
              name: this.myMenuItemName(
                PAGE_PUBLICATION_FEED_GROUP_LIST,
                publicationId,
              ),
              label: "RSS",
              actionValue: `/publication/${publicationId}/feed-groups`,
              icon: "newspaper",
            },
          ],
        },
      ];

      return this.filterVisiblePagesByRole(publicationSubItems);
    },
    filterVisiblePagesByRole(publicationSubItems) {
      const rolePageRules = [
        {
          role: ROLE_ADMIN,
          exclude: [],
        },
        {
          role: ROLE_PRODUCER,
          include: [
            COLLABORATOR_NAV_PAGE_STORIES,
            COLLABORATOR_NAV_PAGE_ADVERTISING,
            COLLABORATOR_NAV_PAGE_ANALYTICS,
            COLLABORATOR_NAV_PAGE_TEAM,
            COLLABORATOR_NAV_PAGE_CHAT_ROOM,
            COLLABORATOR_NAV_PAGE_SUPPORT,
            COLLABORATOR_NAV_PAGE_MY_USERS,
          ],
        },
        {
          role: ROLE_EDITOR,
          exclude: [
            COLLABORATOR_NAV_PAGE_ADVERTISING,
            COLLABORATOR_NAV_PAGE_ANALYTICS,
            COLLABORATOR_NAV_PAGE_REVENUE,
            COLLABORATOR_NAV_PAGE_SUBSCRIPTION_PLANS,
            COLLABORATOR_NAV_PAGE_COURSES,
            COLLABORATOR_NAV_PAGE_SETTINGS,
            COLLABORATOR_NAV_PAGE_BRANDING,
            COLLABORATOR_NAV_PAGE_PRO_FEATURES,
            COLLABORATOR_NAV_PAGE_DEPLOYMENT,
            COLLABORATOR_NAV_PAGE_RSS,
          ],
        },
        {
          role: ROLE_DESIGNER,
          include: [
            COLLABORATOR_NAV_PAGE_STORIES,
          ],
        },
        {
          role: ROLE_CONTRIBUTOR,
          include: [
            COLLABORATOR_NAV_PAGE_STORIES,
          ],
        },
        {
          role: ROLE_MODERATOR,
          include: [
            COLLABORATOR_NAV_PAGE_CHAT_ROOM,
          ],
        },
        {
          role: ROLE_SUPPORT,
          include: [
            COLLABORATOR_NAV_PAGE_SUPPORT,
          ],
        },
        {
          role: ROLE_MARKETER,
          exclude: [
            COLLABORATOR_NAV_PAGE_STORIES,
            COLLABORATOR_NAV_PAGE_ADVERTISING,
            COLLABORATOR_NAV_PAGE_EVENTS,
            COLLABORATOR_NAV_PAGE_ANALYTICS,
            COLLABORATOR_NAV_PAGE_TEAM,
            COLLABORATOR_NAV_PAGE_CHAT_ROOM,
            COLLABORATOR_NAV_PAGE_SUPPORT,
            COLLABORATOR_NAV_PAGE_MY_USERS,
            COLLABORATOR_NAV_PAGE_REVENUE,
            COLLABORATOR_NAV_PAGE_SUBSCRIPTION_PLANS,
            COLLABORATOR_NAV_PAGE_COURSES,
            COLLABORATOR_NAV_PAGE_SETTINGS,
            COLLABORATOR_NAV_PAGE_BRANDING,
            COLLABORATOR_NAV_PAGE_PRO_FEATURES,
            COLLABORATOR_NAV_PAGE_DEPLOYMENT,
            COLLABORATOR_NAV_PAGE_RSS,
            COLLABORATOR_NAV_ITEM_OVERVIEW,
            COLLABORATOR_NAV_ITEM_CREATE_A_NEW_PUBLICATION,
            COLLABORATOR_NAV_ITEM_INVITE_TO_COLLABORATE,
            COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_DRAFT_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_INACTIVE_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_CONTRACTS,
          ],
        },
      ];

      if (this.isOwner) {
        return publicationSubItems;
      }

      const roleRule = rolePageRules.find(rule => rule.role === this.role);

      if (!roleRule) {
        return publicationSubItems;
      }

      if (Array.isArray(roleRule.include) && roleRule.include.length > 0) {
        return publicationSubItems.filter(item => roleRule.include.includes(item.label));
      }

      if (Array.isArray(roleRule.exclude) && roleRule.exclude.length > 0) {
        return publicationSubItems.filter(item => !roleRule.exclude.includes(item.label));
      }

      return publicationSubItems;
    },
    filterVisibleSidePanelItems() {
      const roleSideNavRules = [
        {
          role: ROLE_ADMIN,
          exclude: [],
        },
        {
          role: ROLE_PRODUCER,
          include: [
            COLLABORATOR_NAV_ITEM_OVERVIEW,
            COLLABORATOR_NAV_ITEM_CREATE_A_NEW_PUBLICATION,
            COLLABORATOR_NAV_ITEM_INVITE_TO_COLLABORATE,
            COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_DRAFT_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_INACTIVE_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_CONTRACTS,
          ],
        },
        {
          role: ROLE_EDITOR,
          include: [
            COLLABORATOR_NAV_ITEM_OVERVIEW,
            COLLABORATOR_NAV_ITEM_CREATE_A_NEW_PUBLICATION,
            COLLABORATOR_NAV_ITEM_INVITE_TO_COLLABORATE,
            COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_DRAFT_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_INACTIVE_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_CONTRACTS,
          ],
        },
        {
          role: ROLE_DESIGNER,
          include: [
            COLLABORATOR_NAV_ITEM_OVERVIEW,
            COLLABORATOR_NAV_ITEM_CREATE_A_NEW_PUBLICATION,
            COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_DRAFT_PUBLICATIONS,
            COLLABORATOR_NAV_ITEM_INACTIVE_PUBLICATIONS,
          ],
        },
        {
          role: ROLE_CONTRIBUTOR,
          include: [
            COLLABORATOR_NAV_ITEM_OVERVIEW,
            COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
          ],
        },
        {
          role: ROLE_MODERATOR,
          include: [
            COLLABORATOR_NAV_ITEM_OVERVIEW,
            COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
          ],
        },
        {
          role: ROLE_SUPPORT,
          include: [
            COLLABORATOR_NAV_ITEM_OVERVIEW,
            COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
          ],
        },
        {
          role: ROLE_MARKETER,
          include: [
            COLLABORATOR_NAV_ITEM_OVERVIEW,
            COLLABORATOR_NAV_ITEM_ACTIVE_PUBLICATIONS,
          ],
        },
      ];

      const navItems = this.sideNavPublication;

      if (this.isOwner) {
        return this.sideNavPublication;
      }

      const roleRule = roleSideNavRules.find(rule => rule.role === this.role);

      if (!roleRule) {
        return navItems;
      }

      if (Array.isArray(roleRule.include) && roleRule.include.length > 0) {
        return navItems.filter(item => roleRule.include.includes(item.label));
      }

      if (Array.isArray(roleRule.exclude) && roleRule.exclude.length > 0) {
        return navItems.filter(item => !roleRule.exclude.includes(item.label));
      }

      return navItems;
    },
  },
};
</script>
